import { useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { HebrewTranslateIcon } from '@/components/icons/hebrew-translate-icon'
import { PDFIcon, ImageIcon } from '@/components/icons/mobile-icons'
import { isSignedIn } from '@/lib/baba-auth'
import { useLibrarySource } from '@/lib/library/useLibrarySource'
import { previewFromTranslatedText } from '@/lib/library/mediaResultContent'
import {
  HISTORY_PANEL_CAP,
  HISTORY_LINE1_MAX,
  HISTORY_LINE2_MAX,
  filterHistoryRows,
  groupHistoryRows,
  pickHistorySource,
  rowsFromLedger,
  rowsFromRecents,
  truncatePreview,
  type HistoryFilter,
  type HistoryRow,
  type HistoryRowMedia,
} from '@/lib/library/historyPanelRows'
import { WsButtonGhost, WsKbd, WsSegmented } from './workspace'
import type { RecentTranslation } from './types'

function formatFullWhen(ts: number): string {
  if (!ts) return ''
  return new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function RowIcon({ kind }: { kind: HistoryRow['kind'] }) {
  if (kind === 'pdf') return <PDFIcon size={14} />
  if (kind === 'image') return <ImageIcon size={14} />
  // A touch heavier at 14px, where a 500-weight glyph goes thin next to the
  // filled PDF and image marks.
  return <HebrewTranslateIcon size={14} weight={600} />
}

function HistoryRowButton({
  row,
  active,
  onSelect,
}: {
  row: HistoryRow
  active: boolean
  onSelect: () => void
}) {
  const line1 = truncatePreview(row.inputText, HISTORY_LINE1_MAX)
  const line2 = truncatePreview(row.output, HISTORY_LINE2_MAX)
  const title = `${line1}\n${line2}\n${formatFullWhen(row.ts)}`

  return (
    <button
      type="button"
      onClick={onSelect}
      data-active={active ? 'true' : undefined}
      title={title}
      className={`grid items-start w-full text-left transition-colors duration-150 cursor-pointer select-none rounded-md max-md:min-h-[48px] max-md:items-center text-[13px] max-md:text-[15px] ${active ? '' : 'hover:bg-[var(--card-2)]'}`}
      style={{
        gridTemplateColumns: '16px minmax(0,1fr)',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 6,
        background: active ? 'var(--card)' : 'transparent',
        border: `1px solid ${active ? 'var(--line)' : 'transparent'}`,
      }}
    >
      <span className="mt-[3px]" style={{ color: active ? 'var(--accent-deep)' : 'var(--muted-2)' }}>
        <RowIcon kind={row.kind} />
      </span>
      <span className="min-w-0 flex flex-col justify-center text-left" style={{ direction: 'ltr', textAlign: 'left' }}>
        <bdi
          dir="auto"
          style={{
            display: 'block',
            unicodeBidi: 'plaintext',
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 'inherit',
            fontWeight: 500,
            color: active ? 'var(--accent-deep)' : 'var(--ink)',
            lineHeight: 1.3,
          }}
        >
          {line1}
        </bdi>
        <bdi
          dir="auto"
          style={{
            display: 'block',
            unicodeBidi: 'plaintext',
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 12,
            color: 'var(--muted-2)',
            marginTop: 2,
            lineHeight: 1.3,
          }}
        >
          {line2}
        </bdi>
      </span>
    </button>
  )
}

/**
 * The Translate page's History panel.
 *
 * It reads the account's translations ledger through the SAME
 * `useLibrarySource` hook the Library page uses, so a signed-in person sees
 * the translations they made on their phone, in another browser, or on the
 * Library page — the previous localStorage-only list is what made history
 * look like it was not syncing. Guests, and anyone whose ledger read comes
 * back empty or unreachable, still get this browser's local recents.
 *
 * Text rows restore into the translator. Image and PDF rows have no
 * meaningful input to restore, so the panel hands them UPWARDS and the
 * Translator lays them out in the page itself — original on the left, the
 * structured result on the right. Nothing here opens a popup: the founder's
 * rule is that History never does.
 *
 * Dates live in the group headers, not on the rows, and every row sits flush
 * left: a Hebrew line still reads right-to-left inside its own isolate, but it
 * no longer drags its whole row to the other edge.
 */
export function HistoryPanel({
  recents,
  activeId,
  onSelectText,
  onOpenMedia,
  onClearLocal,
  onRowOpened,
}: {
  /** This browser's local recents — the guest/offline source. */
  recents: RecentTranslation[]
  activeId?: string | null
  onSelectText: (row: HistoryRow) => void
  /** Hands an image/PDF row to the Translator, which owns the result modal. */
  onOpenMedia: (media: HistoryRowMedia) => void
  /** Clears the local recents. Only reachable while the local list is shown;
   * the ledger is account-wide and is cleared from the Library, not from a
   * small button beside a search box. */
  onClearLocal: () => void
  /** Lets the mobile sheet close itself after any row is acted on. */
  onRowOpened?: () => void
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<HistoryFilter>('all')
  const searchRef = useRef<HTMLInputElement | null>(null)

  const signedIn = isSignedIn()
  const lib = useLibrarySource({ enabled: signedIn })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const ledgerRows = useMemo(
    () => rowsFromLedger(lib.history.data, HISTORY_PANEL_CAP, previewFromTranslatedText),
    [lib.history.data],
  )
  const localRows = useMemo(() => rowsFromRecents(recents, HISTORY_PANEL_CAP), [recents])

  const { rows, source } = useMemo(
    () => pickHistorySource({ signedIn, ledgerRows, localRows }),
    [signedIn, ledgerRows, localRows],
  )

  const filtered = useMemo(() => filterHistoryRows(rows, filter, query), [rows, filter, query])
  // Grouped once per list change; the day boundary moving under an open tab
  // is not worth a timer.
  const groups = useMemo(() => groupHistoryRows(filtered, Date.now()), [filtered])

  const handleSelect = (row: HistoryRow) => {
    if (row.media) onOpenMedia(row.media)
    else onSelectText(row)
    onRowOpened?.()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-2 shrink-0">
        <span className="text-[11.5px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted-2)' }}>History</span>
        {source === 'ledger' ? (
          <span className="font-mono" style={{ fontSize: 10.5, color: 'var(--muted-2)' }}>Synced</span>
        ) : (
          rows.length > 0 && (
            <WsButtonGhost h={22} className="text-[11.5px] hover:text-red-500 hover:bg-red-50/50 dark:hover:bg-red-950/20 active:scale-95 cursor-pointer transition-all" onClick={onClearLocal}>Clear</WsButtonGhost>
          )
        )}
      </div>

      <div
        className="touch-44-y flex items-center gap-2 h-[30px] px-2 shrink-0 transition-colors hover:border-[var(--line-strong,var(--line))] focus-within:border-[var(--accent)]"
        style={{ borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--muted-2)' }}
      >
        <Search size={14} aria-hidden />
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search history"
          aria-label="Search history"
          className="h-full flex-1 min-w-0 bg-transparent text-[16px] md:text-[12.5px] focus:outline-none"
          style={{ color: 'var(--ink)' }}
        />
        {!query && <WsKbd>/</WsKbd>}
      </div>

      <div className="flex items-center justify-between my-2.5 shrink-0">
        <WsSegmented
          ariaLabel="Filter history"
          value={filter}
          onChange={setFilter}
          items={[
            { id: 'all', label: 'All' },
            { id: 'text', label: 'Text' },
            { id: 'files', label: 'Files' },
          ]}
        />
        <span className="font-mono" style={{ fontSize: 10.5, color: 'var(--muted-2)' }}>{rows.length}</span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-[12.5px] text-center px-2 py-6" style={{ color: 'var(--muted-2)' }}>
            {rows.length === 0 ? 'Translations you make will appear here.' : 'No matches.'}
          </p>
        ) : (
          groups.map((group, index) => (
            <section key={group.label}>
              <h2
                className="font-mono uppercase"
                style={{
                  margin: index === 0 ? '4px 6px 5px' : '12px 6px 5px',
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  color: 'var(--muted-2)',
                  fontWeight: 600,
                }}
              >
                {group.label}
              </h2>
              <div className="flex flex-col gap-1">
                {group.rows.map((row) => (
                  <HistoryRowButton key={row.id} row={row} active={row.id === activeId} onSelect={() => handleSelect(row)} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </>
  )
}
